import { ArrowLeft } from 'lucide-react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { DiagonalLines } from '@/components/ui/diagonal-lines'
import { Button } from '@/components/ui/button'
import { OddLogoHorizontal } from '@/components/common/OddLogoHorizontal'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { SlotImage } from '@/features/designer/components/SlotImage'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import { SignInForm } from '../components/SignInForm'
import { SignUpForm } from '../components/SignUpForm'
import { safeAuthReturnTo } from '../lib/authRedirect'

export function SignInPage() {
  const location = useLocation()
  const { user, loading } = useAuth()
  const { canDesign, loading: roleLoading } = useUserRole()
  const requestedReturnTo = (location.state as { returnTo?: unknown } | null)?.returnTo
  const returnTo = safeAuthReturnTo(requestedReturnTo)
  const isRegistering = location.pathname === '/register'

  if (loading || (user && roleLoading)) return <LoadingSpinner />
  if (user && !canDesign) return <Navigate to={returnTo} replace />

  return (
    <main className="relative min-h-svh overflow-hidden bg-background lg:grid lg:grid-cols-[minmax(0,1.08fr)_minmax(30rem,0.92fr)]">
      <title>{isRegistering ? 'Create account' : 'Sign in'} — One Day Dance</title>
      <section className="relative hidden min-h-svh overflow-hidden bg-brand-black text-brand-white lg:flex lg:flex-col lg:justify-between lg:p-10 xl:p-14">
        <SlotImage
          slotKey="sign-in-background"
          alt=""
          fadeIn
          className="absolute inset-0 h-full w-full"
          fallback={
            <div className="h-full w-full bg-[linear-gradient(145deg,var(--color-brand-purple-dark),var(--color-brand-black)_58%,var(--color-brand-orange-dark))]" />
          }
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(23,23,23,0.2),rgba(23,23,23,0.88))]" />
        <DiagonalLines
          className="absolute inset-x-0 bottom-0 h-2/5"
          color="#FF9D76"
          opacity={0.22}
          spacing={18}
          strokeWidth={2}
          corner="bottom-left"
        />

        <Link
          to="/"
          aria-label="One Day Dance home"
          className="relative z-10 w-fit rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-white"
        >
          <OddLogoHorizontal height={48} color="currentColor" />
        </Link>

        <div className="relative z-10 max-w-xl space-y-5">
          <p className="max-w-lg text-4xl leading-[1.08] font-semibold tracking-tight xl:text-5xl">
            Get ready to
            <br />
            Move on Film
          </p>
          <p className="max-w-lg text-base leading-relaxed text-brand-white/75 xl:text-lg">
            Access your projects, tickets, applications, and company work through one secure
            account.
          </p>
        </div>
      </section>

      <section className="relative flex min-h-svh flex-col px-5 py-6 sm:px-8 lg:px-12 xl:px-16">
        <div
          className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,var(--color-brand-orange),var(--color-brand-yellow),var(--color-brand-blue),var(--color-brand-purple))]"
          aria-hidden="true"
        />
        <header className="relative z-10 flex items-center justify-between lg:justify-end">
          <Link
            to="/"
            aria-label="One Day Dance home"
            className="rounded-md lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <OddLogoHorizontal
              height={38}
              color="currentColor"
              className="h-7 w-auto min-[420px]:h-8 sm:h-10"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back to the website
          </Link>
        </header>

        <div className="relative z-10 flex flex-1 items-center justify-center pt-6 pb-10 sm:pt-10 sm:pb-14">
          <div className="w-full max-w-md">
            <div className="mb-7 space-y-3 text-center sm:text-left">
              <div>
                <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                  {isRegistering ? 'Create your account' : 'Welcome back'}
                </h1>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                  {isRegistering
                    ? 'Set up your One Day Dance account to get started.'
                    : 'Sign in to continue to your One Day Dance account.'}
                </p>
              </div>
            </div>

            <div className="rounded-3xl border border-border/70 bg-card/92 p-5 shadow-xl shadow-brand-black/5 backdrop-blur sm:p-8 dark:shadow-brand-black/25">
              {canDesign ? (
                <div className="space-y-5 text-center sm:text-left">
                  <div>
                    <p className="font-semibold">Sign-in design preview</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      Hover over the image panel and choose <strong>Assign image</strong> or{' '}
                      <strong>Change image</strong> to edit its media, focus point, and overlay.
                    </p>
                  </div>
                  <Button asChild variant="outline" className="w-full rounded-full">
                    <Link to={returnTo}>Exit preview</Link>
                  </Button>
                </div>
              ) : (
                isRegistering ? (
                  <SignUpForm returnTo={returnTo} />
                ) : (
                  <SignInForm returnTo={returnTo} />
                )
              )}
            </div>

            <p className="mt-5 text-center text-xs leading-relaxed text-muted-foreground">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="underline underline-offset-4 hover:text-foreground">
                Terms
              </Link>{' '}
              and acknowledge our{' '}
              <Link to="/privacy" className="underline underline-offset-4 hover:text-foreground">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>

        <footer className="relative z-10 flex flex-col items-center justify-between gap-2 text-xs text-muted-foreground sm:flex-row">
          <span>© {new Date().getFullYear()} One Day Dance</span>
          <span>
            Need help?{' '}
            <a
              href="mailto:info@onedaydance.com"
              className="font-medium text-foreground underline underline-offset-4"
            >
              info@onedaydance.com
            </a>
          </span>
        </footer>
      </section>
    </main>
  )
}
